/*
 * Hero Takeover — full-screen intro overlay (sections/hero-takeover.liquid,
 * "Takeover" setting) that sits above the navbar. While active, real page
 * scrolling is locked (html/body overflow: hidden) and wheel/touch input
 * is captured instead to drive the fade virtually — scrolling down fades
 * it out, scrolling back up fades it back in — right up until it fully
 * fades out once. At that point it locks for good: scrolling is unlocked
 * (from a real scroll position that never moved, i.e. the top of the
 * page), the dismissal is persisted to localStorage, and the section
 * stays gone for the rest of the pageview. This is what guarantees the
 * next section is fully visible from its own top edge the moment the
 * takeover clears — nothing was scrolled past underneath it while it
 * faded. Modeled on assets/newsletter-popup.js, but persists via
 * localStorage instead of a cookie.
 *
 * Storage is scoped per section instance — the key is
 * `hero-takeover-dismissed:<section.id>` (root element id is
 * `hero-takeover-<section.id>`) — so multiple Hero Takeover sections
 * across different templates track their own dismissal independently.
 * The pre-paint hide for repeat visits is a matching inline <script> in
 * sections/hero-takeover.liquid itself, run before that section's own
 * markup — it also short-circuits in the theme editor so merchants
 * always see the section regardless of a stored dismissal.
 *
 * Loaded globally from snippets/scripts.liquid; binds every
 * [data-hero-takeover] on load and on shopify:section:load.
 */

const SELECTOR = '[data-hero-takeover]';
const INSTANCE_KEY = '_heroTakeoverInstance';
const STORAGE_PREFIX = 'hero-takeover-dismissed:';
const ID_PREFIX = 'hero-takeover-';

class HeroTakeover {
  constructor(root) {
    this.root = root;
    this.storageKey = STORAGE_PREFIX + (root.id || '').replace(ID_PREFIX, '');
    this.reappearDays = parseFloat(root.dataset.reappearDays) || 1;
    // Gesture distance (wheel deltaY / touch px) needed to fade fully out
    // — a fraction of the viewport so it reads as a slow, gradual fade
    // rather than snapping away on the first tick of a gesture.
    this.fadeDistance = Math.max(window.innerHeight * 0.6, 300);
    // Virtual progress driven by captured gestures — real scroll is
    // locked the whole time, so this is the only source of truth.
    this.progress = 0;
    this.touchStartY = null;
    this.locked = false;
    // Set once the fade has completed at least once, so the dismissal
    // key is only written a single time per pageview.
    this.dismissedWritten = false;
    this.onWheel = this.onWheel.bind(this);
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);
    this.init();
  }

  init() {
    const inEditor = Boolean(window.Shopify && window.Shopify.designMode);

    if (!inEditor) {
      // Already hidden pre-paint by the matching inline <script> in
      // sections/hero-takeover.liquid — nothing left for this instance
      // to do.
      let until = null;
      try {
        until = window.localStorage.getItem(this.storageKey);
      } catch (e) {
        // Storage unavailable — fall through and treat as not dismissed.
      }
      if (until && Date.now() < parseInt(until, 10)) {
        return;
      }
    }

    this.lockScroll();
    // Non-passive: preventDefault is required to actually block the
    // gesture from scrolling the real page (overflow: hidden alone can
    // still rubber-band on iOS Safari).
    window.addEventListener('wheel', this.onWheel, { passive: false });
    window.addEventListener('touchstart', this.onTouchStart, { passive: true });
    window.addEventListener('touchmove', this.onTouchMove, { passive: false });
    this.root[INSTANCE_KEY] = this;
  }

  // overflow: hidden blocks every native scroll input at once — wheel,
  // touch, keyboard (Space/Page Down/arrows), and scrollbar dragging —
  // so real scroll position can't move while this is active.
  lockScroll() {
    if (this.locked) return;
    this.locked = true;
    this.prevHtmlOverflow = document.documentElement.style.overflow;
    this.prevBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
  }

  unlockScroll() {
    if (!this.locked) return;
    this.locked = false;
    document.documentElement.style.overflow = this.prevHtmlOverflow;
    document.body.style.overflow = this.prevBodyOverflow;
  }

  onWheel(event) {
    event.preventDefault();
    this.advance(event.deltaY);
  }

  onTouchStart(event) {
    this.touchStartY = event.touches[0].clientY;
  }

  onTouchMove(event) {
    if (this.touchStartY === null) return;
    event.preventDefault();
    const currentY = event.touches[0].clientY;
    // Swiping up (finger moves up the screen) reads as "scroll down", so
    // this matches wheel's positive deltaY convention below.
    const delta = this.touchStartY - currentY;
    this.touchStartY = currentY;
    this.advance(delta);
  }

  // Positive delta (scroll/swipe down) fades out; negative (scroll/swipe
  // up) fades back in — right up until it has fully faded out once.
  advance(delta) {
    if (this.dismissedWritten) return;

    this.progress = Math.min(Math.max(this.progress + delta / this.fadeDistance, 0), 1);
    this.root.style.setProperty('--hero-takeover-opacity', String(1 - this.progress));

    if (this.progress >= 1) {
      this.root.classList.add('is-dismissed');
      this.persistDismissal();
    }
  }

  persistDismissal() {
    this.dismissedWritten = true;
    this.unlockScroll();
    window.removeEventListener('wheel', this.onWheel);
    window.removeEventListener('touchstart', this.onTouchStart);
    window.removeEventListener('touchmove', this.onTouchMove);

    const until = Date.now() + this.reappearDays * 24 * 60 * 60 * 1000;
    try {
      window.localStorage.setItem(this.storageKey, String(until));
    } catch (e) {
      // Storage unavailable (private mode / quota / cross-origin iframe) —
      // the fade above still applies for this pageview, it just won't
      // persist across reloads. Logged so this doesn't look like a no-op.
      console.warn('[hero-takeover] could not persist dismissal to localStorage:', e);
    }
  }

  destroy() {
    this.unlockScroll();
    window.removeEventListener('wheel', this.onWheel);
    window.removeEventListener('touchstart', this.onTouchStart);
    window.removeEventListener('touchmove', this.onTouchMove);
    delete this.root[INSTANCE_KEY];
  }
}

function initAll(scope) {
  scope.querySelectorAll(SELECTOR).forEach((root) => {
    if (root[INSTANCE_KEY]) {
      root[INSTANCE_KEY].destroy();
    }
    new HeroTakeover(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
} else {
  initAll(document);
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) {
    initAll(event.target);
  }
});
